import { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, Tabs, Tab, Typography, Button, Alert, CircularProgress,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, IconButton, Chip, Snackbar, Tooltip, LinearProgress,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import CasinoIcon from '@mui/icons-material/Casino';
import RefreshIcon from '@mui/icons-material/Refresh';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EventNoteIcon from '@mui/icons-material/EventNote';
import {
  getGames, createGame, deleteGame, toggleGameHidden,
  getEntries, deleteEntry,
  runDraw, resetDraw,
  getEmployees, getEmployeeEntries,
} from '../api';

function TabPanel({ value, index, children }) {
  return value === index ? <Box sx={{ pt: 3 }}>{children}</Box> : null;
}

const fmtDate = (str) => {
  if (!str) return '—';
  const parts = str.split('-');
  if (parts.length !== 3) return str;
  const [y, m, d] = parts.map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric'
  });
};

// ── CSV parser ─────────────────────────────────────────────────────────────
const parseCSV = (text) => {
  const lines = text.trim().split('\n').map(l => l.trim()).filter(Boolean);
  if (lines.length < 2) throw new Error('CSV must have a header row and at least one data row.');

  const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
  const opponentIdx = headers.indexOf('opponent');
  const dateIdx = headers.indexOf('date');
  const timeIdx = headers.indexOf('time');
  const deadlineIdx = headers.indexOf('deadline');

  if (opponentIdx === -1 || dateIdx === -1)
    throw new Error('CSV must have "opponent" and "date" columns.');

  return lines.slice(1).map((line, i) => {
    const cols = line.split(',').map(c => c.trim());
    const opponent = (cols[opponentIdx] || '').replace(/^"|"$/g, '').trim();
    const date = (cols[dateIdx] || '').replace(/^"|"$/g, '').trim();
    if (!opponent || !date) throw new Error(`Row ${i + 2}: missing opponent or date.`);
    return {
      opponent,
      date,
      time: timeIdx !== -1 ? cols[timeIdx] || '' : '',
      deadline: deadlineIdx !== -1 ? cols[deadlineIdx] || '' : '',
    };
  });
};

// ── Games Tab ──────────────────────────────────────────────────────────────
function GamesTab() {
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [snack, setSnack] = useState(null);

  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState({ opponent: '', date: '', time: '', deadline: '' });
  const [adding, setAdding] = useState(false);
  const [addError, setAddError] = useState(null);

  const [drawGame, setDrawGame] = useState(null);
  const [drawing, setDrawing] = useState(false);

  const fileInputRef = useRef();
  const [importing, setImporting] = useState(false);
  const [importProgress, setImportProgress] = useState(null);
  const [importErrors, setImportErrors] = useState([]);
  const [importSummaryOpen, setImportSummaryOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getGames();
      setGames(data.sort((a, b) => new Date(a.date) - new Date(b.date)));
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleAdd = async () => {
    if (!form.opponent.trim() || !form.date) {
      setAddError('Opponent and date are required.');
      return;
    }
    try {
      setAdding(true);
      setAddError(null);
      await createGame(form);
      setAddOpen(false);
      setForm({ opponent: '', date: '', time: '', deadline: '' });
      setSnack('Game added.');
      load();
    } catch (e) { setAddError(e.message); }
    finally { setAdding(false); }
  };

  const handleDelete = async (gameId, opponent) => {
    if (!window.confirm(`Delete game vs. ${opponent}?`)) return;
    try {
      await deleteGame(gameId);
      setSnack('Game deleted.');
      load();
    } catch (e) { setSnack(`Error: ${e.message}`); }
  };

  const handleToggleHidden = async (game) => {
    try {
      await toggleGameHidden(game.gameId, !game.hidden);
      setSnack(game.hidden ? `${game.opponent} is now visible to employees.` : `${game.opponent} is now hidden from employees.`);
      load();
    } catch (e) { setSnack(`Error: ${e.message}`); }
  };

  const handleDraw = async () => {
    try {
      setDrawing(true);
      await runDraw(drawGame.gameId);
      setDrawGame(null);
      setSnack('Draw complete.');
      load();
    } catch (e) { setSnack(`Error: ${e.message}`); }
    finally { setDrawing(false); }
  };

  const handleResetDraw = async (gameId) => {
    if (!window.confirm('Reset this draw? Win counts will NOT be decremented.')) return;
    try {
      await resetDraw(gameId);
      setSnack('Draw reset.');
      load();
    } catch (e) { setSnack(`Error: ${e.message}`); }
  };

  const handleCSVFile = async (e) => {
    const file = e.target.files[0];
    e.target.value = '';
    if (!file) return;

    let rows;
    try {
      const text = await file.text();
      rows = parseCSV(text);
    } catch (e) {
      setSnack(`CSV parse error: ${e.message}`);
      return;
    }

    setImporting(true);
    setImportProgress({ done: 0, total: rows.length });
    setImportErrors([]);

    const errs = [];
    for (let i = 0; i < rows.length; i++) {
      try {
        await createGame(rows[i]);
      } catch (e) {
        errs.push(`Row ${i + 2} (${rows[i].opponent}): ${e.message}`);
      }
      setImportProgress({ done: i + 1, total: rows.length });
    }

    setImporting(false);
    setImportProgress(null);
    setImportErrors(errs);
    setImportSummaryOpen(true);
    load();
  };

  if (loading) return <CircularProgress />;
  if (error) return <Alert severity="error">{error}</Alert>;

  const imported = importProgress ? importProgress.done : 0;
  const total = importProgress ? importProgress.total : 0;

  return (
    <>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mb: 2 }}>
        <input type="file" accept=".csv" ref={fileInputRef} style={{ display: 'none' }} onChange={handleCSVFile} />
        <Button variant="outlined" startIcon={<UploadFileIcon />} onClick={() => fileInputRef.current.click()} disabled={importing}>
          Import CSV
        </Button>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setAddOpen(true)}>
          Add Game
        </Button>
      </Box>

      {importing && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Importing {imported} of {total}…
          </Typography>
          <LinearProgress variant="determinate" value={(imported / total) * 100} />
        </Box>
      )}

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell><b>Opponent</b></TableCell>
              <TableCell><b>Date</b></TableCell>
              <TableCell><b>Time</b></TableCell>
              <TableCell><b>Deadline</b></TableCell>
              <TableCell><b>Status</b></TableCell>
              <TableCell align="right"><b>Actions</b></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {games.length === 0 && (
              <TableRow>
                <TableCell colSpan={6}>
                  <Typography color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>No games yet.</Typography>
                </TableCell>
              </TableRow>
            )}
            {games.map(g => (
              <TableRow
                key={g.gameId}
                sx={{ opacity: g.hidden ? 0.5 : 1, bgcolor: g.hidden ? 'action.hover' : 'inherit' }}
              >
                <TableCell>
                  {g.opponent}
                  {g.hidden && <Chip label="Hidden" size="small" sx={{ ml: 1 }} />}
                </TableCell>
                <TableCell>{fmtDate(g.date)}</TableCell>
                <TableCell>{g.time || '—'}</TableCell>
                <TableCell>{fmtDate(g.deadline)}</TableCell>
                <TableCell>
                  {g.result
                    ? <Chip label="Drawn" color="success" size="small" />
                    : <Chip label="Open" color="primary" size="small" />}
                </TableCell>
                <TableCell align="right">
                  {!g.result ? (
                    <Tooltip title="Run draw">
                      <IconButton size="small" color="primary" onClick={() => setDrawGame(g)}>
                        <CasinoIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  ) : (
                    <Tooltip title="Reset draw">
                      <IconButton size="small" onClick={() => handleResetDraw(g.gameId)}>
                        <RefreshIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  <Tooltip title={g.hidden ? 'Show to employees' : 'Hide from employees'}>
                    <IconButton size="small" onClick={() => handleToggleHidden(g)}>
                      {g.hidden
                        ? <VisibilityIcon fontSize="small" />
                        : <VisibilityOffIcon fontSize="small" />}
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete game">
                    <IconButton size="small" color="error" onClick={() => handleDelete(g.gameId, g.opponent)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add Game Dialog */}
      <Dialog open={addOpen} onClose={() => setAddOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Add Game</DialogTitle>
        <DialogContent>
          {addError && <Alert severity="error" sx={{ mb: 2 }}>{addError}</Alert>}
          <TextField label="Opponent" fullWidth value={form.opponent}
            onChange={e => setForm(f => ({ ...f, opponent: e.target.value }))}
            sx={{ mt: 1, mb: 2 }} />
          <TextField label="Date" type="date" fullWidth value={form.date}
            onChange={e => setForm(f => ({ ...f, date: e.target.value }))}
            InputLabelProps={{ shrink: true }} sx={{ mb: 2 }} />
          <TextField label="Time (optional)" type="time" fullWidth value={form.time}
            onChange={e => setForm(f => ({ ...f, time: e.target.value }))}
            InputLabelProps={{ shrink: true }} sx={{ mb: 2 }} />
          <TextField label="Entry Deadline (optional)" type="date" fullWidth value={form.deadline}
            onChange={e => setForm(f => ({ ...f, deadline: e.target.value }))}
            InputLabelProps={{ shrink: true }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleAdd} disabled={adding}>
            {adding ? <CircularProgress size={20} /> : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Draw Confirm Dialog */}
      <Dialog open={!!drawGame} onClose={() => setDrawGame(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Run Draw</DialogTitle>
        <DialogContent>
          <Typography>
            Run the weighted lottery draw for <b>vs. {drawGame?.opponent}</b>?
            This will select winners from all current entries.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDrawGame(null)}>Cancel</Button>
          <Button variant="contained" onClick={handleDraw} disabled={drawing}>
            {drawing ? <CircularProgress size={20} /> : 'Run Draw'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Import Summary Dialog */}
      <Dialog open={importSummaryOpen} onClose={() => setImportSummaryOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Import Complete</DialogTitle>
        <DialogContent>
          {importErrors.length === 0 ? (
            <Alert severity="success">All games imported successfully.</Alert>
          ) : (
            <>
              <Alert severity="warning" sx={{ mb: 2 }}>
                {importErrors.length} row{importErrors.length !== 1 ? 's' : ''} failed to import.
              </Alert>
              {importErrors.map((err, i) => (
                <Typography key={i} variant="body2" color="error">{err}</Typography>
              ))}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={() => setImportSummaryOpen(false)}>Done</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!snack} autoHideDuration={4000} onClose={() => setSnack(null)} message={snack} />
    </>
  );
}

// ── Signups Tab ────────────────────────────────────────────────────────────
function SignupsTab() {
  const [games, setGames] = useState([]);
  const [selectedGame, setSelectedGame] = useState('');
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [snack, setSnack] = useState(null);

  useEffect(() => {
    getGames()
      .then(data => setGames(data.sort((a, b) => new Date(a.date) - new Date(b.date))))
      .catch(() => {});
  }, []);

  const loadEntries = useCallback(async (gameId) => {
    try {
      setLoading(true);
      const data = await getEntries(gameId);
      setEntries(data);
    } catch (e) { setEntries([]); }
    finally { setLoading(false); }
  }, []);

  const handleGameChange = (gameId) => {
    setSelectedGame(gameId);
    if (gameId) loadEntries(gameId);
    else setEntries([]);
  };

  const handleRemove = async (employeeName) => {
    if (!window.confirm(`Remove ${employeeName} from this game?`)) return;
    try {
      await deleteEntry(selectedGame, employeeName);
      setSnack(`Removed ${employeeName}.`);
      loadEntries(selectedGame);
    } catch (e) { setSnack(`Error: ${e.message}`); }
  };

  return (
    <>
      <TextField
        select
        label="Select Game"
        value={selectedGame}
        onChange={e => handleGameChange(e.target.value)}
        sx={{ mb: 3, minWidth: 280 }}
        SelectProps={{ native: true }}
        InputLabelProps={{ shrink: true }}
      >
        <option value="">— Select a game —</option>
        {games.map(g => (
          <option key={g.gameId} value={g.gameId}>vs. {g.opponent} ({g.date})</option>
        ))}
      </TextField>

      {loading && <CircularProgress />}

      {!loading && selectedGame && (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell><b>Name</b></TableCell>
                <TableCell><b>Tickets Requested</b></TableCell>
                <TableCell><b>Entered At</b></TableCell>
                <TableCell align="right"><b>Actions</b></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {entries.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4}>
                    <Typography color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>No entries yet.</Typography>
                  </TableCell>
                </TableRow>
              )}
              {entries.map(e => (
                <TableRow key={e.employeeName}>
                  <TableCell>{e.employeeName}</TableCell>
                  <TableCell>{e.ticketsRequested}</TableCell>
                  <TableCell>{new Date(e.enteredAt).toLocaleString()}</TableCell>
                  <TableCell align="right">
                    <Tooltip title="Remove entry">
                      <IconButton size="small" color="error" onClick={() => handleRemove(e.employeeName)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Snackbar open={!!snack} autoHideDuration={4000} onClose={() => setSnack(null)} message={snack} />
    </>
  );
}

// ── History Tab ────────────────────────────────────────────────────────────
function HistoryTab() {
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getGames()
      .then(data => setGames(
        data.filter(g => g.result)
          .sort((a, b) => new Date(b.result.drawnAt) - new Date(a.result.drawnAt))
      ))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <CircularProgress />;

  return (
    <TableContainer component={Paper} variant="outlined">
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell><b>Game</b></TableCell>
            <TableCell><b>Date</b></TableCell>
            <TableCell><b>Drawn At</b></TableCell>
            <TableCell><b>Winners</b></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {games.length === 0 && (
            <TableRow>
              <TableCell colSpan={4}>
                <Typography color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>No draws yet.</Typography>
              </TableCell>
            </TableRow>
          )}
          {games.map(g => (
            <TableRow key={g.gameId}>
              <TableCell>vs. {g.opponent}</TableCell>
              <TableCell>{fmtDate(g.date)}</TableCell>
              <TableCell>{new Date(g.result.drawnAt).toLocaleString()}</TableCell>
              <TableCell>
                {g.result.winners.map((w, i) => (
                  <Typography key={i} variant="body2">
                    {w.name} ({w.ticketsAwarded} ticket{w.ticketsAwarded !== 1 ? 's' : ''})
                  </Typography>
                ))}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

// ── Employees Tab ──────────────────────────────────────────────────────────
function EmployeesTab() {
  const [employees, setEmployees] = useState([]);
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [drawer, setDrawer] = useState({ open: false, employee: null, entries: [], loading: false });

  useEffect(() => {
    Promise.all([getEmployees(), getGames()])
      .then(([emps, gms]) => {
        setEmployees(emps.sort((a, b) => b.winCount - a.winCount));
        setGames(gms);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleViewEntries = async (emp) => {
    setDrawer({ open: true, employee: emp, entries: [], loading: true });
    try {
      const data = await getEmployeeEntries(emp.employeeName);
      setDrawer(prev => ({ ...prev, entries: data.entries, loading: false }));
    } catch {
      setDrawer(prev => ({ ...prev, loading: false }));
    }
  };

  if (loading) return <CircularProgress />;

  return (
    <>
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell><b>Name</b></TableCell>
              <TableCell><b>Win Count</b></TableCell>
              <TableCell align="center"><b>Games Entered</b></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {employees.length === 0 && (
              <TableRow>
                <TableCell colSpan={3}>
                  <Typography color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>No employees yet.</Typography>
                </TableCell>
              </TableRow>
            )}
            {employees.map(e => (
              <TableRow key={e.employeeName}>
                <TableCell>{e.employeeName}</TableCell>
                <TableCell>{e.winCount}</TableCell>
                <TableCell align="center">
                  <Tooltip title="View entered games">
                    <IconButton size="small" onClick={() => handleViewEntries(e)}>
                      <EventNoteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog
        open={drawer.open}
        onClose={() => setDrawer(prev => ({ ...prev, open: false }))}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Games entered — {drawer.employee?.employeeName}</DialogTitle>
        <DialogContent dividers>
          {drawer.loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress />
            </Box>
          ) : drawer.entries.length === 0 ? (
            <Typography color="text.secondary">No entries found.</Typography>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell><b>Opponent</b></TableCell>
                  <TableCell><b>Date</b></TableCell>
                  <TableCell align="center"><b>Tickets Requested</b></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {drawer.entries.map(entry => {
                  const game = games.find(g => g.gameId === entry.gameId);
                  return (
                    <TableRow key={entry.gameId}>
                      <TableCell>{game?.opponent ?? '—'}</TableCell>
                      <TableCell>{game ? fmtDate(game.date) : '—'}</TableCell>
                      <TableCell align="center">{entry.ticketsRequested}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDrawer(prev => ({ ...prev, open: false }))}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

// ── Root AdminView ─────────────────────────────────────────────────────────
export default function AdminView() {
  const [tab, setTab] = useState(0);

  return (
    <>
      <Typography variant="h5" fontWeight={700} gutterBottom>
        Admin Panel
      </Typography>
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)}>
          <Tab label="Games" />
          <Tab label="Signups" />
          <Tab label="History" />
          <Tab label="Employees" />
        </Tabs>
      </Box>
      <TabPanel value={tab} index={0}><GamesTab /></TabPanel>
      <TabPanel value={tab} index={1}><SignupsTab /></TabPanel>
      <TabPanel value={tab} index={2}><HistoryTab /></TabPanel>
      <TabPanel value={tab} index={3}><EmployeesTab /></TabPanel>
    </>
  );
}
